import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { FieldGroup } from '@/components/ui/field';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import {
  type ShippingAddressFormData,
  shippingAddressFormId,
  shippingAddressFormSchema,
} from '@/types/user-profile.type';

import { ShippingAddressFormFields } from '../ShippingAddressFormFields';

export const Shipping = () => {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const { contactAddressLine1, contactAddressLine2, contactCity, contactState, contactZip } =
    useSessionStore();
  const { nextStep } = useProductSelectionStep();

  const form = useForm<ShippingAddressFormData>({
    resolver: zodResolver(shippingAddressFormSchema),
    defaultValues: {
      addressLine1: contactAddressLine1,
      addressLine2: contactAddressLine2,
      city: contactCity,
      state: contactState,
      zip: contactZip,
    },
  });

  const onSubmit = async (data: ShippingAddressFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      contactAddressLine1: data.addressLine1,
      contactAddressLine2: data.addressLine2,
      contactCity: data.city,
      contactState: data.state,
      contactZip: data.zip,
    });

    // Auto-save session (non-blocking)
    saveSession();

    navigate({ to: nextStep });
  };

  const handleBack = () => {
    navigate({ to: '/insurance/benefits' });
  };

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Results" />

      <Card>
        <CardHeader>
          <CardTitle>Where should we ship the supplies?</CardTitle>
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form
              className="space-y-8 sm:space-y-12"
              onSubmit={form.handleSubmit(onSubmit)}
              id={shippingAddressFormId}
            >
              <FieldGroup>
                <ShippingAddressFormFields formId={shippingAddressFormId} />
              </FieldGroup>

              <Button type="submit" className="w-full sm:w-auto sm:min-w-[186px]">
                Continue
              </Button>
            </form>
          </FormProvider>
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
};
